<template>
  <div class="i-meeting-note-card">
    <div
      class="career-stage"
      :class="{ alt: props.sameStageAsLast }"
    >
      <template v-if="!props.sameStageAsLast">
        <div
          class="badge"
          :style="{ borderColor: careerStageInfo.color }"
        >
          <img
            alt="Icon to represent your career stage at this session"
            :src="careerStageInfo.icon"
          />
        </div>
        <div :style="{ color: careerStageInfo.color }">
          {{ careerStageInfo.label }}
        </div>
      </template>
      <template v-else>
        <div
          class="line short"
          :style="{ backgroundColor: careerStageInfo.color }"
        ></div>
        <div
          class="circle"
          :style="{ backgroundColor: careerStageInfo.color }"
        ></div>
        <div
          v-if="props.sameStageAsNext"
          class="line"
          :style="{ backgroundColor: careerStageInfo.color }"
        ></div>
      </template>
    </div>
    <div class="info">
      <div class="top">
        <div class="first">
          {{ title }}
          <iUserMeetingFeedback
            class="rate"
            :meeting-note-id="props.meetingNotes.id"
            :meeting-title="title"
            :meeting-date="date"
            :user-meeting-score="props.meetingNotes.userMeetingScore ? props.meetingNotes.userMeetingScore : undefined"
            :user-meeting-notes="props.meetingNotes.userMeetingNotes ? props.meetingNotes.userMeetingNotes : undefined"
            :created-at="createdAt"
            :show-immediately="showFeedbackImmediately"
            @refresh-meeting-notes="$emit('refresh-meeting-notes')"
          />
        </div>
        <div class="date">{{ date }}</div>
      </div>
      <div class="zoom">
        <img
          class="icon"
          alt="Zoom icon"
          :src="iconZoom"
        />
        <div
          v-if="props.meetingNotes.recordingUrl"
          class="recording"
        >
          <a
            class="link"
            :href="props.meetingNotes.recordingUrl"
            target="_blank"
            >Session Recording
          </a>
          <div
            v-if="props.meetingNotes.recordingPasscode"
            class="passcode"
          >
            Passcode:
            <iBaseCopyText :text="props.meetingNotes.recordingPasscode" />
          </div>
        </div>
        <div
          v-else
          class="empty"
        >
          No session recording.
        </div>
      </div>
    </div>
    <div class="connector">
      <template v-if="props.sameStageAsNext">
        <div
          v-if="!props.sameStageAsLast"
          class="circle"
          :style="{ backgroundColor: careerStageInfo.color }"
        ></div>
        <div
          class="line"
          :style="{ backgroundColor: careerStageInfo.color }"
        ></div>
      </template>
    </div>
    <div class="meeting-content">
      <div
        v-if="hasAnyMeetingContent"
        class="expandable-content"
      >
        <iBaseExpandableParagraph
          :text="combinedMeetingContent"
          text-size="unset"
          :collapsed-length="isTabletOrLarger && combinedMeetingContent.length < 280 ? 280 : descriptionMaxlength"
          :cta-color="azureRadiance500"
          :show-icon="false"
        />
      </div>
      <div
        v-else
        class="empty"
      >
        No meeting notes available.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { azureRadiance500 } from "@/styles/colors"
import { breakpointLg, breakpointSm, breakpointMd, breakpointXl } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import { careerStageData } from "@/helpers/careerStageData"

import iBaseCopyText from "@/components/base/BaseCopyText.vue"
import iBaseExpandableParagraph from "@/components/base/BaseExpandableParagraph.vue"
import iUserMeetingFeedback from "@/components/coaching/home/UserMeetingFeedback.vue"

// ASSETS
import iconZoom from "@/assets/icons/icon-zoom.svg"

// TYPES
import { MeetingNotesType, MeetingNotesUserAccess } from "@/shared/types/MeetingNotes"

// EMITS
const emit = defineEmits(["refresh-meeting-notes"])

// PROPS
const props = defineProps({
  isLatest: {
    type: Boolean,
    default: false
  },
  meetingNotes: {
    type: Object as () => MeetingNotesUserAccess,
    required: true
  },
  sameStageAsLast: {
    type: Boolean,
    required: false
  },
  sameStageAsNext: {
    type: Boolean,
    required: false
  }
})

// COMPOSABLES
const isMobileOrLarger = useIsScreenSize(breakpointSm)
const isTabletOrLarger = useIsScreenSize(breakpointMd)
const isDesktopOrLarger = useIsScreenSize(breakpointLg)
const isXlOrLarger = useIsScreenSize(breakpointXl)

// CONSTANTS
const MEETING_TYPE_MAPPER: { [x in MeetingNotesType]?: string } = {
  discovery: "Discovery Session with",
  coaching: "Coaching Session with",
  executiveCoaching: "Executive Coaching Session with",
  recurring: "Coaching Session with",
  scoping: "Discovery Session with",
  scopingRescheduled: "Discovery Session with",
  onDemand: "On-Demand Session with",
  proPackage: "Resume delivered by"
}
const LOCALE_DATE_STRING_OPTIONS = {
  year: "numeric",
  month: "long",
  day: "numeric",
  timeZoneName: "short",
  hour: "2-digit",
  minute: "2-digit"
} as const

//COMPUTED
const descriptionMaxlength = computed(() =>
  isXlOrLarger.value
    ? 350
    : isDesktopOrLarger.value
      ? 280
      : isTabletOrLarger.value
        ? 350
        : isMobileOrLarger.value
          ? 200
          : 100
)
const careerStageInfo = computed(() => careerStageData[props.meetingNotes.careerStage || "exploring"])
const date = computed(() => new Date(props.meetingNotes.date).toLocaleString("en-US", LOCALE_DATE_STRING_OPTIONS))
const title = computed(() => `${MEETING_TYPE_MAPPER[props.meetingNotes.type]} ${props.meetingNotes.coachName}`)
const createdAt = computed(() =>
  props.meetingNotes.userFeedbackCreatedAt ? new Date(props.meetingNotes.userFeedbackCreatedAt) : undefined
)
const showFeedbackImmediately = computed(() => {
  const lastTriggeredMeetingId = parseInt(localStorage.getItem("last-triggered-meeting-id") || "0")
  if (!lastTriggeredMeetingId) localStorage.setItem("last-triggered-meeting-id", props.meetingNotes.id.toString())

  return props.isLatest && lastTriggeredMeetingId !== props.meetingNotes.id
})
const hasAnyMeetingContent = computed(() => {
  return !!(
    props.meetingNotes.actionItems ||
    (props.meetingNotes.nextSteps && props.meetingNotes.nextSteps.length > 0) ||
    props.meetingNotes.overview
  )
})
const combinedMeetingContent = computed(() => {
  const sections = []

  // Add action items (coach's personal notes) without title
  if (props.meetingNotes.actionItems) {
    sections.push(props.meetingNotes.actionItems.trim())
  }

  // Add next steps with title
  if (props.meetingNotes.nextSteps && props.meetingNotes.nextSteps.length > 0) {
    const nextStepsSection = [
      "<strong>Next Steps</strong>",
      // No <p> tag and a new line character indicates it's from the zoom meeting summaries table not meeting notes, handle accordingly
      ...(props.meetingNotes.nextSteps.includes("\n") && !props.meetingNotes.nextSteps.includes("<p>")
        ? ["<ul>", ...props.meetingNotes.nextSteps.split("\n").map((step) => `<li>${step}</li>`), "</ul>"]
        : [props.meetingNotes.nextSteps.trim()])
    ].join("")
    sections.push(nextStepsSection)
  }

  // Add overview with title
  if (props.meetingNotes.overview) {
    const overviewSection = ["<strong>Meeting Overview</strong>", `<p>${props.meetingNotes.overview.trim()}</p>`].join(
      ""
    )
    sections.push(overviewSection)
  }

  return sections.join("<p>--------------------</p>")
})
</script>

<style lang="scss" scoped>
.i-meeting-note-card {
  @include grid($cols: 50px 1fr, $rows: auto 1fr, $align-items: stretch, $gap: 0 20px);

  width: 100%;

  @media screen and (min-width: $breakpoint-md) {
    @include grid($cols: 80px 1fr, $rows: auto 1fr, $align-items: stretch, $gap: 0 25px);
  }

  .empty {
    color: $black400;
  }

  .circle {
    @include border-radius-rounded;

    width: 10px;
    height: 10px;
    flex: 0 0 10px;
  }

  .line {
    width: 1px;
    height: 100%;

    &.short {
      height: 10px;
    }
  }

  .career-stage {
    @include flex(column, start, center, $gap: 8px);
    @include text-xs;

    font-weight: 600;

    @media screen and (min-width: $breakpoint-md) {
      @include text-md;
    }

    &.alt {
      gap: 0;
    }

    .badge {
      @include flex(column, center, center);
      @include border-radius-sm;

      border-width: 2px;
      border-style: solid;
      width: 40px;
      height: 40px;

      @media screen and (min-width: $breakpoint-md) {
        @include border-radius-md;

        width: 50px;
        height: 50px;
      }
    }
  }

  .info {
    @include flex(column, start, stretch, $gap: 12px);

    .top {
      @include flex(column, start, stretch, $gap: 5px);
      @include text-md;

      font-weight: 600;

      .first {
        @include flex(column-reverse, start, start, $gap: 5px);

        position: relative;

        @media screen and (min-width: $breakpoint-sm) {
          @include flex(row, space-between, start, $gap: 20px);
        }

        .rate {
          width: 100%;
          top: -24px;
          position: absolute;

          @media screen and (min-width: $breakpoint-sm) {
            width: unset;
            top: unset;
            position: relative;
          }
        }
      }

      .date {
        @include text-sm;

        font-weight: 400;
      }
    }

    .zoom {
      @include flex(row, start, start, $gap: 10px);

      .icon {
        width: 23px;
      }

      .recording {
        @include flex(column, start, start);

        .link {
          @include text-md;

          font-weight: 500;
          color: $azure-radiance600;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
            filter: brightness(85%);
          }
        }

        .passcode {
          @include flex(row, start, start, $gap: 5px);
          @include text-xs;

          color: $comet800;
        }
      }
    }
  }

  .connector {
    @include flex(column, start, center);
  }

  .meeting-content {
    border-bottom: 1px solid $zumthor100;
    padding-bottom: 16px;
    margin-bottom: 30px;

    @media screen and (min-width: $breakpoint-sm) {
      margin-bottom: 25px;
    }

    .empty {
      margin-top: 16px;
    }
  }
}
</style>

<style lang="scss">
.i-meeting-note-card {
  .meeting-content {
    .expandable-content {
      p {
        margin: 0;
      }

      ol {
        margin: 5px 0;
      }

      ul {
        margin: 5px 0;
      }
    }
  }
}
</style>
